import React, { useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Button, Input, Divider } from 'antd';
import AuthShell from './AuthShell';
import { GoogleLogo, MicrosoftLogo } from './logos';
import {
  requestEmailOtp,
  goToGoogleSignup,
  goToMicrosoftSignup,
} from './authApi';
import { isValidEmail, emailLengthError } from './emailValidation';
import { readReturnPath } from './returnPath';

// Enjo-style "Create your account": provider sign-up on top, email below.
const TrooprSignUp = (props) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  // Bumped per submit so a repeated, identical error remounts and is re-announced.
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);

  // A deep link can land on sign-up too (via the sign-in footer link).
  const returnPath = readReturnPath(props.location.search);

  // Length problems are shown while typing; anything else waits for submit so
  // a half-typed address isn't called invalid. The live one wins when both
  // exist — it is the more specific, and the actionable, message.
  const lengthError = emailLengthError(email);
  const shownError = lengthError || error;

  const submit = async () => {
    setAttempt((n) => n + 1);
    setError('');
    if (lengthError) return;
    if (!isValidEmail(email)) {
      return setError('Please enter a valid email address');
    }
    setLoading(true);
    try {
      const res = await requestEmailOtp(email.trim(), { isSignup: true });
      setLoading(false);
      if (res.data && res.data.success) {
        props.history.push({
          pathname: '/verification',
          state: {
            sessionId: res.data.sessionId,
            email: email.trim(),
            isSignup: true,
            returnTo: returnPath,
          },
        });
      } else {
        setError((res.data && res.data.message) || 'Could not send code. Try again.');
      }
    } catch (e) {
      setLoading(false);
      // Surface a server-supplied reason (e.g. a rejected address) instead of
      // the generic retry copy, which tells the user to retry something that
      // can never succeed.
      const msg = e && e.response && e.response.data && e.response.data.message;
      setError(msg || 'Something went wrong. Please try again.');
    }
  };

  return (
    <AuthShell>
      <h1 className="tpr-auth__title">Create your Troopr account</h1>
      <p className="tpr-auth__subtitle">Use your work email to create your account</p>

      <div className="tpr-auth__providers">
        <Button
          className="tpr-auth__provider"
          onClick={() => goToGoogleSignup(returnPath)}
        >
          <GoogleLogo /> Sign up with Google
        </Button>
        <Button
          className="tpr-auth__provider"
          onClick={() => goToMicrosoftSignup(returnPath)}
        >
          <MicrosoftLogo /> Sign up with Microsoft
        </Button>
      </div>

      <Divider className="tpr-auth__divider">OR continue with</Divider>

      <div className="tpr-auth__field">
        <Input
          className="tpr-auth__input"
          type="email"
          name="email"
          id="email"
          autoComplete="email"
          aria-label="Work email"
          aria-required="true"
          placeholder="name@work-email.com"
          aria-invalid={shownError ? 'true' : undefined}
          aria-describedby={shownError ? 'email-error' : undefined}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Drop a stale server/submit error the moment they start fixing it.
            if (error) setError('');
          }}
          onPressEnter={submit}
        />
        {/* Below the input, and derived from the current value rather than
            stored — so it clears itself the moment the address fits again. */}
        {shownError && (
          <span
            key={attempt}
            className="tpr-auth__error"
            id="email-error"
            role="alert"
          >
            * {shownError}
          </span>
        )}
      </div>

      <Button
        className="tpr-auth__primary"
        type="primary"
        loading={loading}
        disabled={!!lengthError}
        onClick={submit}
      >
        Continue
      </Button>

      <div className="tpr-auth__footer">
        Already have an account?{' '}
        {/* A real link so it is keyboard-focusable (WCAG 2.1.1). */}
        <Link
          className="tpr-auth__link"
          to={{ pathname: '/troopr/access', state: { returnTo: returnPath } }}
        >
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
};

export default withRouter(TrooprSignUp);
